import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Eye, X, Printer, XCircle, Calendar, CalendarDays, Edit2, Play, Pause, ShoppingBag, Hash, DollarSign, Plus, Minus, Trash2, Clock, ShoppingCart, ChevronDown, ChevronLeft, ChevronRight, List, ClipboardPaste } from 'lucide-react';
import useSortable from '../hooks/useSortable';
import SortableHeader from '../components/SortableHeader';
import useDateFilter from '../hooks/useDateFilter';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import UiDateField from '../components/ui/UiDateField';
import UiSearchField from '../components/ui/UiSearchField';
import UiTimeField from '../components/ui/UiTimeField';
import { UiSummaryCard, UiSummaryCards } from '../components/ui/UiSummaryCard';
import { UiViewToggle } from '../components/ui/UiTabs';
import { toIsoDateString } from '../utils/dateFormat';
import { buildStandardPresetOptions, shiftDateRangeByPreset } from '../utils/dateRangeUi';
import { formatUiCurrencyCompact, formatUiCurrencyDetailed, formatUiDate, formatUiDateTimeRange, formatUiMonthYear, formatUiTime } from '../utils/uiFormat';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import OrderReceiptModal from '../components/OrderReceiptModal';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { getOrderSearchText } from '../utils/salesSearch';

const normalizeModifierLink = (link) => (
  link?.modifier_detail || {
    id: link?.modifier || link?.id,
    name: link?.modifier_name || link?.display_name || link?.name,
    display_name: link?.modifier_name || link?.display_name || link?.name,
  }
);

const SALES_PAGE_SIZE = 20000;
const SALES_FETCH_LIMIT = 150000;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [businessDayStartHour, setBusinessDayStartHour] = useState(6);
  
  // Standardized date filter hook - initialized after businessDayStartHour
  const {
    preset: dateFilter,
    setPreset: setDateFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    shiftRange: shiftDateFilterRange,
    applyPreset,
    getDateRangeDisplay,
    getPeriodParts,
    PRESETS: DATE_PRESETS,
  } = useDateFilter('today', businessDayStartHour);
  const periodParts = getPeriodParts();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ payment_method: '', discount_percent: 0, notes: '' });
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeData, setCompleteData] = useState({ payment_method: 'cash', amount_received: '' });
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [menuItemFilter, setMenuItemFilter] = useState('');
  const [modifierFilter, setModifierFilter] = useState('');
  const [modifierOptionFilter, setModifierOptionFilter] = useState('');
  const [servingSizeFilter, setServingSizeFilter] = useState('');
  const [servingSizeOptions, setServingSizeOptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [menuItemModifiers, setMenuItemModifiers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [loadedOrderCount, setLoadedOrderCount] = useState(0);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [ordersTruncated, setOrdersTruncated] = useState(false);
  const [calendarOrders, setCalendarOrders] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarSummary, setCalendarSummary] = useState({ byDate: {}, byMonth: {} });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const receiptRef = useRef();
  // Smart Paste Previous Sales
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [smartPasteText, setSmartPasteText] = useState('');
  const [smartPasteDate, setSmartPasteDate] = useState('');
  const [smartParsedRows, setSmartParsedRows] = useState([]);
  const [smartPasteMenuItems, setSmartPasteMenuItems] = useState([]);
  const [smartParsing, setSmartParsing] = useState(false);
  const [smartSubmitting, setSmartSubmitting] = useState(false);
  const modifierSourceMenuItem = useMemo(() => {
    if (menuItemFilter) {
      return menuItems.find(item => String(item.id) === String(menuItemFilter)) || null;
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;
    const exact = menuItems.find(item => String(item.name || '').trim().toLowerCase() === query);
    if (exact) return exact;
    const matches = menuItems.filter(item => String(item.name || '').toLowerCase().includes(query));
    return matches.length === 1 ? matches[0] : null;
  }, [menuItemFilter, menuItems, searchQuery]);

  const selectedModifier = useMemo(
    () => menuItemModifiers.find(modifier => String(modifier.id) === String(modifierFilter)) || null,
    [menuItemModifiers, modifierFilter]
  );

  const modifierOptionOptions = useMemo(
    () => (selectedModifier?.options || []).filter(option => option?.is_active !== false && option?.is_available !== false),
    [selectedModifier]
  );
  const menuItemMetaById = useMemo(() => {
    const categoryById = new Map(categories.map(category => [String(category.id), category]));
    return Object.fromEntries(menuItems.map(item => {
      const categoryId = item?.category ? String(item.category) : '';
      const category = categoryById.get(categoryId);
      return [String(item.id), {
        categoryId,
        departmentId: category?.department ? String(category.department) : '',
      }];
    }));
  }, [categories, menuItems]);
  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        map[toIsoDateString(cursor)] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);
  const getHolidayForDate = (dateStr) => holidayMap[dateStr] || null;

  // CSV import state (manual entry now uses the full POS screen via /pos?mode=backdate)

  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const hasScopedItemFilter = Boolean(
    searchQuery.trim() || departmentFilter || categoryFilter || menuItemFilter || modifierFilter || modifierOptionFilter || servingSizeFilter
  );
  const requiresRowData = viewMode === 'table' || hasScopedItemFilter;

  const formatDateValueLocal = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const addCalendarDays = (dateStr, days) => {
    const date = new Date(`${dateStr}T12:00:00`);
    date.setDate(date.getDate() + days);
    return formatDateValueLocal(date);
  };
  const filterEndCalendarDate = endDate ? addCalendarDays(endDate, 1) : '';

  useEffect(() => {
    if (!startDate) return;
    const baseDate = new Date(`${startDate}T12:00:00`);
    if (Number.isNaN(baseDate.getTime())) return;
    setCalendarMonth({ year: baseDate.getFullYear(), month: baseDate.getMonth() });
  }, [startDate, dateFilter]);

  const handleDateFilterChange = (preset) => {
    if (viewMode === 'calendar' && ['month', 'year'].includes(preset)) {
      const base = new Date(calendarMonth.year, calendarMonth.month, 1);
      const start = preset === 'year'
        ? formatDateValueLocal(new Date(base.getFullYear(), 0, 1))
        : formatDateValueLocal(new Date(base.getFullYear(), base.getMonth(), 1));
      const end = preset === 'year'
        ? formatDateValueLocal(new Date(base.getFullYear(), 11, 31))
        : formatDateValueLocal(new Date(base.getFullYear(), base.getMonth() + 1, 0));
      handleSalesCalendarPeriodChange({
        mode: preset,
        startDate: start,
        endDate: end,
        year: base.getFullYear(),
        month: preset === 'year' ? 0 : base.getMonth(),
      });
      return;
    }
    setDateFilter(preset);
    applyPreset(preset);
  };

  const handleSalesCalendarPeriodChange = ({ mode, startDate: nextStartDate, endDate: nextEndDate, year, month }) => {
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setCalendarMonth({ year, month: mode === 'year' ? 0 : month });
    setDateFilter(mode === 'year' ? 'year' : 'month');
  };

  const shiftSalesDateRange = (delta) => {
    if (viewMode !== 'calendar') {
      shiftDateFilterRange(delta);
      return;
    }
    const shifted = shiftDateRangeByPreset({
      preset: dateFilter,
      startDate,
      endDate,
      delta,
    });
    const base = new Date(`${shifted.startDate}T12:00:00`);
    if (Number.isNaN(base.getTime())) return;
    handleSalesCalendarPeriodChange({
      mode: dateFilter === 'year' ? 'year' : 'month',
      startDate: shifted.startDate,
      endDate: shifted.endDate,
      year: base.getFullYear(),
      month: dateFilter === 'year' ? 0 : base.getMonth(),
    });
  };

  useEffect(() => {
    if (viewMode === 'calendar' && !['month', 'year'].includes(dateFilter)) {
      handleDateFilterChange('month');
    }
  }, [viewMode, dateFilter]);

  const dateFilterOptions = viewMode === 'calendar'
    ? buildStandardPresetOptions(['month', 'year'])
    : buildStandardPresetOptions(['today', 'week', 'month', 'year', 'custom']);

  useEffect(() => {
    if (requiresRowData) {
      fetchOrders();
    } else {
      setLoading(false);
      setOrders([]);
      setLoadedOrderCount(0);
      setTotalOrderCount(0);
      setOrdersTruncated(false);
    }
    fetchSettings();
    fetchSummary();
  }, [statusFilter, dateFilter, startDate, endDate, startTime, endTime, departmentFilter, categoryFilter, requiresRowData]);

  useEffect(() => {
    fetchCalendarOrders();
  }, [viewMode, dateFilter, calendarMonth.year, calendarMonth.month, statusFilter, departmentFilter, categoryFilter, searchQuery, menuItemFilter, modifierFilter, modifierOptionFilter, servingSizeFilter]);

  useEffect(() => {
    api.get('/departments/').then(res => setDepartments(res.data.results || res.data)).catch(() => {});
    api.get('/categories/').then(res => setCategories(res.data.results || res.data)).catch(() => {});
    api.get('/menu-items/?page_size=500&active=true').then(res => setMenuItems(res.data.results || res.data)).catch(() => {});
    api.get('/modifiers/?page_size=500&active=true').then(res => setModifiers(res.data.results || res.data)).catch(() => {});
    api.get('/serving-size-options/?page_size=500').then(res => setServingSizeOptions(res.data.results || res.data)).catch(() => {});
    api.get('/public-holidays/?page_size=500').then(res => setPublicHolidays(res.data.results || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const applyModifierOptions = (options) => {
      setMenuItemModifiers(options);
      if (modifierFilter && !options.some(option => String(option.id) === String(modifierFilter))) {
        setModifierFilter('');
        setModifierOptionFilter('');
      }
    };

    if (modifierSourceMenuItem?.id) {
      const linkedModifiers = (modifierSourceMenuItem.modifiers || []).map(normalizeModifierLink).filter(modifier => modifier?.id);
      if (linkedModifiers.length) {
        applyModifierOptions(linkedModifiers);
        return;
      }
      api.get(`/menu-items/${modifierSourceMenuItem.id}/modifier-groups/`).then(modRes => {
        const modifierData = modRes.data || [];
        const modifierGroups = modifierData.map(normalizeModifierLink).filter(modifier => modifier?.id);
        applyModifierOptions(modifierGroups);
      }).catch(() => applyModifierOptions([]));
    } else {
      applyModifierOptions([]);
    }
  }, [modifierFilter, modifierSourceMenuItem]);

  useEffect(() => {
    if (modifierOptionFilter && !modifierOptionOptions.some(option => String(option.id) === String(modifierOptionFilter))) {
      setModifierOptionFilter('');
    }
  }, [modifierOptionFilter, modifierOptionOptions]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/');
      setSettings(res.data);
      const h = res.data?.business_day_start_hour ?? 6;
      setBusinessDayStartHour(h);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const buildFilterUrl = (base, options = {}) => {
    const rangeStartDate = options.startDate ?? startDate;
    const rangeEndDate = options.endDate ?? filterEndCalendarDate;
    const includeTime = options.includeTime ?? true;
    let url = `${base}?`;
    if (statusFilter && !['discounted', 'previous', 'cash', 'digital'].includes(statusFilter)) url += `status=${statusFilter}&`;
    if (departmentFilter) url += `department_id=${departmentFilter}&`;
    if (categoryFilter) url += `category_id=${categoryFilter}&`;

    if (rangeStartDate && rangeEndDate) {
      url += `start_date=${rangeStartDate}&end_date=${rangeEndDate}&`;
      if (includeTime) {
        if (startTime) url += `start_time=${startTime}&`;
        if (endTime) url += `end_time=${endTime}&`;
      }
    }
    return url;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const baseUrl = buildFilterUrl('/orders/');
      const allOrders = [];
      let page = 1;
      let totalCount = 0;
      let hasNextPage = true;

      while (hasNextPage && allOrders.length < SALES_FETCH_LIMIT) {
        const res = await api.get(`${baseUrl}page=${page}&page_size=${SALES_PAGE_SIZE}`);
        const payload = res.data;
        const pageResults = payload?.results || payload || [];

        if (!Array.isArray(pageResults)) break;

        allOrders.push(...pageResults);
        totalCount = payload?.count || totalCount || pageResults.length;

        if (Array.isArray(payload?.results)) {
          hasNextPage = Boolean(payload.next) && pageResults.length > 0;
          page += 1;
        } else {
          hasNextPage = false;
        }
      }

      setOrders(allOrders.slice(0, SALES_FETCH_LIMIT));
      setLoadedOrderCount(Math.min(allOrders.length, SALES_FETCH_LIMIT));
      setTotalOrderCount(totalCount || allOrders.length);
      setOrdersTruncated((totalCount || allOrders.length) > SALES_FETCH_LIMIT);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const url = buildFilterUrl('/orders/summary/');
      const res = await api.get(url);
      setSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const { sortKey: oSortKey, sortDir: oSortDir, handleSort: oHandleSort, sortData: oSortData } = useSortable('created_at', 'desc');
  const { sortKey: srSortKey, sortDir: srSortDir, handleSort: srHandleSort, sortData: srSortData } = useSortable('total', 'desc');

  const getItemAmount = (item) => {
    const directTotal = parseFloat(item.total_price ?? item.total ?? '');
    if (!Number.isNaN(directTotal)) return directTotal;
    const qty = parseFloat(item.quantity || 0);
    const unit = parseFloat(item.unit_price ?? item.price ?? 0);
    return qty * unit;
  };

  const fetchCalendarOrders = async () => {
    if (viewMode !== 'calendar') return;
    setCalendarLoading(true);
    try {
      const needsClientRowCalendar = hasScopedItemFilter;
      const fetchCalendarRows = async (rangeStart, rangeEnd) => {
        const baseUrl = buildFilterUrl('/orders/', {
          startDate: rangeStart,
          endDate: rangeEnd,
          includeTime: false,
        });
        const allCalendarOrders = [];
        let page = 1;
        let hasNextPage = true;

        while (hasNextPage && allCalendarOrders.length < SALES_FETCH_LIMIT) {
          const res = await api.get(`${baseUrl}page=${page}&page_size=${SALES_PAGE_SIZE}`);
          const payload = res.data;
          const pageResults = payload?.results || payload || [];

          if (!Array.isArray(pageResults)) break;

          allCalendarOrders.push(...pageResults);

          if (Array.isArray(payload?.results)) {
            hasNextPage = Boolean(payload.next) && pageResults.length > 0;
            page += 1;
          } else {
            hasNextPage = false;
          }
        }

        return allCalendarOrders.slice(0, SALES_FETCH_LIMIT);
      };

      if (!needsClientRowCalendar) {
        const rangeStart = dateFilter === 'year'
          ? `${calendarMonth.year}-01-01`
          : formatDateValueLocal(new Date(calendarMonth.year, calendarMonth.month, 1));
        const rangeEnd = dateFilter === 'year'
          ? `${calendarMonth.year}-12-31`
          : formatDateValueLocal(new Date(calendarMonth.year, calendarMonth.month + 1, 0));
        const summaryUrl = buildFilterUrl('/orders/calendar_summary/', {
          startDate: rangeStart,
          endDate: rangeEnd,
          includeTime: false,
        });
        try {
          const res = await api.get(summaryUrl);
          const payload = res.data || {};
          setCalendarSummary({
            byDate: Object.fromEntries((payload.by_date || []).map((row) => [row.date, row])),
            byMonth: Object.fromEntries((payload.by_month || []).map((row) => [row.month, row])),
          });
          setCalendarOrders([]);
        } catch (summaryError) {
          console.error('Error fetching aggregated calendar sales:', summaryError);
          const fallbackOrders = await fetchCalendarRows(rangeStart, rangeEnd);
          setCalendarOrders(fallbackOrders);
          setCalendarSummary({ byDate: {}, byMonth: {} });
        }
      } else {
        const monthStart = dateFilter === 'year'
          ? `${calendarMonth.year}-01-01`
          : formatDateValueLocal(new Date(calendarMonth.year, calendarMonth.month, 1));
        const monthEndExclusive = dateFilter === 'year'
          ? `${calendarMonth.year + 1}-01-01`
          : formatDateValueLocal(new Date(calendarMonth.year, calendarMonth.month + 1, 1));
        const fallbackOrders = await fetchCalendarRows(monthStart, monthEndExclusive);
        setCalendarOrders(fallbackOrders);
        setCalendarSummary({ byDate: {}, byMonth: {} });
      }
    } catch (error) {
      console.error('Error fetching calendar sales:', error);
      toast.error('Failed to load calendar sales');
      setCalendarSummary({ byDate: {}, byMonth: {} });
    } finally {
      setCalendarLoading(false);
    }
  };

  const getOrderSalesFactor = (order) => {
    if (order?.status === 'cancelled') return -1;
    if (order?.status === 'completed') return 1;
    return 0;
  };

  const getOrderCountFactor = (order) => (
    order?.status === 'completed' ? 1 : 0
  );

  const getSignedOrderAmount = (order) => parseFloat(order?.total_amount || 0) * getOrderSalesFactor(order);
  const getSignedOrderItemCount = (order) => ((order?.items || []).reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) || 0) * getOrderSalesFactor(order);

  const getItemSearchText = (item) => [
    item.menu_item_name,
    item.notes,
    ...(item.modifier_selections || []).flatMap(selection => [
      selection.modifier_group_name,
      selection.modifier_option_name,
      selection.raw_material_name,
    ]),
  ].filter(Boolean).join(' ').toLowerCase();

  const itemMatchesSalesFilters = (item, includeSearch = true) => {
    const itemMeta = menuItemMetaById[String(item.menu_item)] || {};
    const itemMatchesSearch = includeSearch && searchQuery.trim()
      ? getItemSearchText(item).includes(searchQuery.trim().toLowerCase())
      : true;
    const itemMatchesDepartment = departmentFilter
      ? String(itemMeta.departmentId || '') === String(departmentFilter)
      : true;
    const itemMatchesCategory = categoryFilter
      ? String(itemMeta.categoryId || '') === String(categoryFilter)
      : true;
    const itemMatchesMenuItem = menuItemFilter
      ? String(item.menu_item) === String(menuItemFilter)
      : true;
    const itemMatchesModifier = modifierFilter
      ? (item.modifier_selections || []).some(selection => String(selection.modifier_group) === String(modifierFilter))
      : true;
    const itemMatchesModifierOption = modifierOptionFilter
      ? (item.modifier_selections || []).some(selection => String(selection.modifier_option) === String(modifierOptionFilter))
      : true;
    const itemMatchesServingSize = servingSizeFilter
      ? String(item.portion_name || '') === String(servingSizeFilter)
      : true;

    return itemMatchesSearch
      && itemMatchesDepartment
      && itemMatchesCategory
      && itemMatchesMenuItem
      && itemMatchesModifier
      && itemMatchesModifierOption
      && itemMatchesServingSize;
  };

  const getMatchingModifierLabels = (item) => {
    const query = searchQuery.trim().toLowerCase();
    const itemNameMatches = query
      ? [item.menu_item_name, item.notes].filter(Boolean).join(' ').toLowerCase().includes(query)
      : true;
    const labels = new Set();

    (item.modifier_selections || []).forEach(selection => {
      const matchesModifier = modifierFilter ? String(selection.modifier_group) === String(modifierFilter) : true;
      const matchesModifierOption = modifierOptionFilter ? String(selection.modifier_option) === String(modifierOptionFilter) : true;
      if (!matchesModifier || !matchesModifierOption) return;

      const label = [selection.modifier_group_name, selection.modifier_option_name].filter(Boolean).join(': ');
      const selectionText = [
        selection.modifier_group_name,
        selection.modifier_option_name,
        selection.raw_material_name,
      ].filter(Boolean).join(' ').toLowerCase();

      if (!query || itemNameMatches || selectionText.includes(query) || modifierFilter || modifierOptionFilter) {
        labels.add(label || selection.modifier_option_name || selection.modifier_group_name || 'Modifier option');
      }
    });

    return Array.from(labels);
  };

  const matchesSalesOrderFilters = (order) => {
    const matchesSearch = getOrderSearchText(order).includes(searchQuery.toLowerCase());

    let matchesMenuItem = true;
    if (menuItemFilter) {
      matchesMenuItem = (order.items || []).some(item => String(item.menu_item) === menuItemFilter);
    }

    let matchesModifier = true;
    if (modifierFilter) {
      matchesModifier = (order.items || []).some(item =>
        (item.modifier_selections || []).some(selection => String(selection.modifier_group) === modifierFilter)
      );
    }

    let matchesModifierOption = true;
    if (modifierOptionFilter) {
      matchesModifierOption = (order.items || []).some(item =>
        (item.modifier_selections || []).some(selection => String(selection.modifier_option) === String(modifierOptionFilter))
      );
    }

    let matchesServingSize = true;
    if (servingSizeFilter) {
      matchesServingSize = (order.items || []).some(item => String(item.portion_name || '') === String(servingSizeFilter));
    }

    // 'discounted', 'previous', 'cash', 'digital' are client-side pseudo-statuses.
    let matchesStatusExtra = true;
    if (statusFilter === 'discounted') matchesStatusExtra = parseFloat(order.discount_amount || 0) > 0;
    else if (statusFilter === 'previous') matchesStatusExtra = (order.notes || '').includes('[Previous Sale:');
    else if (statusFilter === 'cash') matchesStatusExtra = (order.payment_method || '') === 'cash';
    else if (statusFilter === 'digital') matchesStatusExtra = ['digital', 'digital_wallet', 'card', 'online'].includes(order.payment_method || '');

    return matchesSearch && matchesMenuItem && matchesModifier && matchesModifierOption && matchesServingSize && matchesStatusExtra;
  };

  const getOrderSortValue = (order, key) => {
    if (key === 'item_count') {
      return getSignedOrderItemCount(order);
    }
    return order?.[key];
  };

  const filteredOrders = oSortData(orders.filter(matchesSalesOrderFilters), getOrderSortValue);
  const filteredCalendarOrders = useMemo(
    () => calendarOrders.filter(matchesSalesOrderFilters),
    [calendarOrders, searchQuery, menuItemFilter, modifierFilter, modifierOptionFilter, servingSizeFilter]
  );

  // Compute summary from filtered orders
  const filteredSummary = useMemo(() => {
    if (!requiresRowData && !hasScopedItemFilter) {
      return {
        total_orders: summary?.total_orders || 0,
        total_items_sold: summary?.total_items_sold || 0,
        total_amount: parseFloat(summary?.total_amount || 0),
      };
    }

    const totalOrders = filteredOrders.reduce((sum, order) => sum + getOrderCountFactor(order), 0);
    let totalItemsSold = 0;
    let totalAmount = 0;

    filteredOrders.forEach(order => {
      const salesFactor = getOrderSalesFactor(order);
      if (!hasScopedItemFilter) {
        totalAmount += parseFloat(order.total_amount || 0) * salesFactor;
      }

      let matchingItemAmount = 0;
      let matchingItemQty = 0;
      (order.items || []).forEach(item => {
        if (itemMatchesSalesFilters(item)) {
          matchingItemQty += parseFloat(item.quantity || 0);
          matchingItemAmount += getItemAmount(item);
        }
      });

      totalItemsSold += matchingItemQty * salesFactor;
      if (hasScopedItemFilter) {
        totalAmount += matchingItemAmount * salesFactor;
      }
    });

    return {
      total_orders: totalOrders,
      total_items_sold: totalItemsSold,
      total_amount: parseFloat(totalAmount.toFixed(2))
    };
  }, [filteredOrders, hasScopedItemFilter, requiresRowData, summary]);

  const salesSearchSummaryRows = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const byItem = {};

    filteredOrders.forEach(order => {
      const salesFactor = getOrderSalesFactor(order);
      (order.items || []).forEach(item => {
        if (!itemMatchesSalesFilters(item)) return;

        const itemName = item.menu_item_name || 'Unknown Item';
        if (!byItem[itemName]) {
          byItem[itemName] = {
            itemName,
            modifierLabels: new Set(),
            count: 0,
            total: 0,
          };
        }

        const labels = getMatchingModifierLabels(item);
        labels.forEach(label => byItem[itemName].modifierLabels.add(label));
        byItem[itemName].count += parseFloat(item.quantity || 0) * salesFactor;
        byItem[itemName].total += getItemAmount(item) * salesFactor;
      });
    });

    return Object.values(byItem)
      .map(row => ({
        ...row,
        modifierLabels: Array.from(row.modifierLabels).sort(),
        modifiersText: Array.from(row.modifierLabels).sort().join(', '),
      }))
      .sort((a, b) => b.total - a.total || a.itemName.localeCompare(b.itemName));
  }, [filteredOrders, searchQuery, menuItemFilter, modifierFilter, modifierOptionFilter, servingSizeFilter, departmentFilter, categoryFilter]);
  const sortedSalesSearchSummaryRows = useMemo(
    () => srSortData(salesSearchSummaryRows),
    [salesSearchSummaryRows, srSortData]
  );

  const [collapsedDates, setCollapsedDates] = useState(new Set());

  const ordersGroupedByDate = useMemo(() => {
    if (dateFilter === 'today') return null;
    const groups = {};
    filteredOrders.forEach(order => {
      const key = toIsoDateString(order.created_at);
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(order);
    });
    return Object.fromEntries(Object.entries(groups).sort(([a], [b]) => b > a ? 1 : -1));
  }, [filteredOrders, dateFilter]);

  const calendarYear = calendarMonth.year;
  const calendarSourceOrders = viewMode === 'calendar'
    ? filteredCalendarOrders
    : filteredOrders;
  const salesCalendarData = useMemo(() => {
    if (viewMode === 'calendar' && !hasScopedItemFilter) {
      return {
        byDate: calendarSummary.byDate || {},
        byMonth: calendarSummary.byMonth || {},
      };
    }

    const byDate = {};
    const byMonth = {};

    calendarSourceOrders.forEach(order => {
      const dateKey = toIsoDateString(order.created_at);
      if (!dateKey) return;
      const salesFactor = getOrderSalesFactor(order);
      const orderCountFactor = getOrderCountFactor(order);

      const matchingItems = hasScopedItemFilter
        ? (order.items || []).filter(item => itemMatchesSalesFilters(item))
        : (order.items || []);
      const amount = hasScopedItemFilter
        ? matchingItems.reduce((sum, item) => sum + getItemAmount(item), 0) * salesFactor
        : parseFloat(order.total_amount || 0) * salesFactor;
      const itemCount = matchingItems.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) * salesFactor;

      if (!byDate[dateKey]) {
        byDate[dateKey] = { orders: 0, total: 0, items: 0 };
      }
      byDate[dateKey].orders += orderCountFactor;
      byDate[dateKey].total += amount;
      byDate[dateKey].items += itemCount;

      const monthKey = dateKey.substring(0, 7);
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { orders: 0, total: 0, items: 0 };
      }
      byMonth[monthKey].orders += orderCountFactor;
      byMonth[monthKey].total += amount;
      byMonth[monthKey].items += itemCount;
    });

    return { byDate, byMonth };
  }, [calendarSourceOrders, hasScopedItemFilter, viewMode, calendarSummary]);

  const changeCalendarMonth = (delta) => {
    const next = new Date(calendarMonth.year, calendarMonth.month + delta, 1);
    handleSalesCalendarPeriodChange({
      mode: 'month',
      startDate: formatDateValueLocal(new Date(next.getFullYear(), next.getMonth(), 1)),
      endDate: formatDateValueLocal(new Date(next.getFullYear(), next.getMonth() + 1, 0)),
      year: next.getFullYear(),
      month: next.getMonth(),
    });
  };

  const toggleDate = (date) => setCollapsedDates(prev => {
    const next = new Set(prev);
    if (next.has(date)) next.delete(date); else next.add(date);
    return next;
  });
  const collapseAll = () => setCollapsedDates(new Set(Object.keys(ordersGroupedByDate || {})));
  const expandAll = () => setCollapsedDates(new Set());
  const fmtDateGroup = (dateStr) => formatUiDate(dateStr);

  const viewOrder = async (order) => {
    try {
      const res = await api.get(`/orders/${order.id}/receipt/`);
      setSelectedOrder({ ...order, receipt: res.data });
      setShowModal(true);
    } catch (error) {
      setSelectedOrder(order);
      setShowModal(true);
    }
  };

  const cancelOrder = async (order) => {
    if (!window.confirm('Cancel this sale? Stock will be restored.')) return;
    try {
      await api.post(`/orders/${order.id}/cancel/`);
      toast.success('Sale cancelled');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to cancel sale');
    }
  };

  const handleEditSale = async () => {
    try {
      await api.post(`/orders/${selectedOrder.id}/edit_sale/`, editData);
      toast.success('Sale amended successfully');
      setShowEditModal(false);
      setShowModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to amend sale');
    }
  };

  const openEditModal = (order) => {
    // Pending orders: resume in POS screen so cashier can modify items
    if (order.status === 'pending') {
      navigate(`/pos?edit_order=${order.id}`);
      return;
    }
    // Completed orders: show edit modal for payment/discount/notes changes
    const pm = order.payment_method === 'split' ? 'cash' : (order.payment_method || 'cash');
    setEditData({
      payment_method: pm,
      discount_percent: parseFloat(order.discount_percent) || 0,
      notes: order.notes || ''
    });
    setSelectedOrder(order);
    setShowEditModal(true);
  };

  const completeHeldSale = async () => {
    try {
      const data = {
        payment_method: completeData.payment_method,
        amount_received: parseFloat(completeData.amount_received) || selectedOrder.total_amount
      };
      await api.post(`/orders/${selectedOrder.id}/complete_held/`, data);
      toast.success('Sale completed successfully!');
      setShowCompleteModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to complete sale');
    }
  };

  const openCompleteModal = (order) => {
    setSelectedOrder(order);
    setCompleteData({ payment_method: 'cash', amount_received: '' });
    setShowCompleteModal(true);
  };

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
  });

  // --- Add Previous Sale: opens full POS screen in backdate mode ---
  const openAddPrevSale = () => navigate('/pos?mode=backdate');

  const isPreviousSale = (order) => (order.notes || '').includes('[Previous Sale:');

  const deleteSale = async (order) => {
    if (!window.confirm(`Permanently delete order ${order.order_number}? This erases the record entirely and restores inventory. This cannot be undone.\n\n(To reverse a sale returned by a customer, use Cancel instead.)`)) return;
    try {
      await api.post(`/orders/${order.id}/delete_sale/`);
      toast.success('Sale deleted and inventory restored');
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  // Smart Paste handlers
  const parseSmartPaste = async () => {
    if (!smartPasteText.trim()) { toast.error('Paste some text first'); return; }
    setSmartParsing(true);
    try {
      const res = await api.post('/orders/parse_smart_paste/', {
        text: smartPasteText,
        sale_date: smartPasteDate,
      });
      setSmartParsedRows(res.data.parsed || []);
      setSmartPasteMenuItems(res.data.menu_items || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to parse');
    } finally {
      setSmartParsing(false);
    }
  };

  const updateSmartRow = (index, field, value) => {
    setSmartParsedRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'matched_id' && value) {
        const mi = smartPasteMenuItems.find(m => String(m.id) === String(value));
        if (mi) updated[index].matched_item = mi.name;
      }
      return updated;
    });
  };

  const removeSmartRow = (index) => {
    setSmartParsedRows(prev => prev.filter((_, i) => i !== index));
  };

  const submitSmartPaste = async () => {
    const validRows = smartParsedRows.filter(r => r.matched_id && r.total_price);
    if (!validRows.length) { toast.error('No valid rows to submit'); return; }
    if (!smartPasteDate) { toast.error('Select a sale date'); return; }
    setSmartSubmitting(true);
    try {
      const grouped = {};
      for (const row of validRows) {
        const key = `${smartPasteDate}|${row.time || '12:00'}`;
        if (!grouped[key]) grouped[key] = { date: smartPasteDate, time: row.time || '12:00', items: [] };
        grouped[key].items.push({
          menu_item_id: parseInt(row.matched_id),
          quantity: parseInt(row.quantity) || 1,
          unit_price: parseFloat(row.total_price) / (parseInt(row.quantity) || 1),
        });
      }
      let created = 0;
      for (const g of Object.values(grouped)) {
        await api.post('/orders/add_previous_sale/', {
          sale_date: g.date,
          sale_time: g.time,
          payment_method: 'cash',
          items: g.items,
        });
        created++;
      }
      toast.success(`${created} previous sale(s) added`);
      setShowSmartPaste(false);
      setSmartPasteText('');
      setSmartParsedRows([]);
      setSmartPasteDate('');
      fetchOrders();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit');
    } finally {
      setSmartSubmitting(false);
    }
  };

  const formatCurrencyCompact = (amount) => formatUiCurrencyCompact(amount, settings?.currency_symbol || 'Rs.');
  const formatCurrencyDetailed = (amount) => formatUiCurrencyDetailed(amount, settings?.currency_symbol || 'Rs.');
  const formatCalendarSalesCompact = (amount) => (
    formatUiCurrencyCompact(amount, settings?.currency_symbol || 'Rs.').replace(/^Rs\.\s*/, '')
  );

  const SalesCalendarLegend = () => (
    <div className="mt-4 flex flex-wrap gap-4 border-t pt-3 text-xs text-gray-500">
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary-500" /> Amount (Rs.)</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Orders</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Items</span>
    </div>
  );

  const renderCalendarView = () => {
    const todayKey = toIsoDateString(new Date());
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (dateFilter === 'year') {
      return (
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">{calendarYear} Sales Calendar</h3>
            <p className="text-xs text-gray-400">Click a month card to jump back to the table</p>
          </div>
          <span className="text-xs text-gray-500">
            {hasScopedItemFilter
              ? `${filteredOrders.length.toLocaleString()} sales in range`
              : '12-month aggregate view'}
          </span>
        </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {monthShort.map((label, index) => {
              const monthKey = `${calendarYear}-${String(index + 1).padStart(2, '0')}`;
              const monthInfo = salesCalendarData.byMonth[monthKey] || { orders: 0, total: 0, items: 0 };
              const monthStart = `${monthKey}-01`;
              const monthEnd = formatDateValueLocal(new Date(calendarYear, index + 1, 0));
              return (
                <button
                  key={monthKey}
                  type="button"
                  onClick={() => {
                    setStartDate(monthStart);
                    setEndDate(monthEnd);
                    setDateFilter('custom');
                    setViewMode('table');
                  }}
                  className="text-left rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 hover:border-primary-300 hover:shadow-sm transition"
                >
                  <div className="text-sm font-semibold text-gray-800">{label}</div>
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center gap-1.5 text-xl font-bold text-primary-600"><span className="h-2.5 w-2.5 rounded-full bg-primary-500" />{formatCalendarSalesCompact(monthInfo.total)}</div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />{monthInfo.orders.toLocaleString()}</div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700"><span className="h-2 w-2 rounded-full bg-amber-500" />{(monthInfo.items ?? 0).toLocaleString()}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <SalesCalendarLegend />
        </div>
      );
    }

    const firstDow = (new Date(calendarMonth.year, calendarMonth.month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(calendarMonth.year, calendarMonth.month + 1, 0).getDate();
    const cells = Array(firstDow).fill(null);
    for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => changeCalendarMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
            <ChevronLeft size={18} />
          </button>
          <div className="text-center">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">{formatUiMonthYear(`${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-01`)}</h3>
            <p className="text-xs text-gray-400">Click a day card to jump back to the table</p>
          </div>
          <button onClick={() => changeCalendarMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
            <ChevronRight size={18} />
          </button>
        </div>
        {calendarLoading && <p className="mb-3 text-xs text-gray-400">Loading full month sales...</p>}
        <div className="grid grid-cols-7 gap-1 text-center mb-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-xs text-gray-400 font-medium py-1">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="min-h-[84px] rounded-xl bg-gray-50/70" />;
            }

            const dateKey = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayInfo = salesCalendarData.byDate[dateKey] || { orders: 0, total: 0, items: 0 };
            const holiday = getHolidayForDate(dateKey);
            const hasSales = Math.abs(dayInfo.total || 0) > 0.009 || dayInfo.orders > 0 || Math.abs(dayInfo.items || 0) > 0.009;
            const isToday = dateKey === todayKey;
            const isPast = dateKey < todayKey;

            return (
              <button
                key={dateKey}
                type="button"
                onClick={() => {
                  if (!hasSales) return;
                  setStartDate(dateKey);
                  setEndDate(dateKey);
                  setDateFilter('custom');
                  setViewMode('table');
                }}
                className={`min-h-[84px] rounded-xl border p-2 text-left transition ${
                  hasSales
                    ? 'bg-primary-50 border-primary-200 hover:border-primary-400 hover:shadow-sm'
                    : holiday
                      ? 'bg-amber-50 border-amber-300'
                      : isPast
                        ? 'bg-gray-300 border-gray-400'
                        : 'bg-gray-50 border-gray-100'
                } ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-primary-500' : ''}`}
              >
                <div className={`text-xs font-bold ${hasSales ? 'text-primary-700' : 'text-gray-400'}`}>{day}</div>
                {holiday && <div className="mt-1 text-[9px] text-amber-700 font-semibold truncate">{holiday.name}</div>}
                {hasSales ? (
                  <div className="mt-2 space-y-0.5">
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 leading-tight"><span className="h-1.5 w-1.5 rounded-full bg-primary-500" />{formatCalendarSalesCompact(dayInfo.total)}</div>
                    <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{dayInfo.orders.toLocaleString()}</div>
                    <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-700"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{dayInfo.items.toLocaleString()}</div>
                  </div>
                ) : (
                  <div className="mt-6 text-[10px] text-gray-300">{monthShort[calendarMonth.month]}</div>
                )}
              </button>
            );
          })}
        </div>
        <SalesCalendarLegend />
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Sales</h1>
          <p className="text-sm sm:text-base text-gray-500">View and manage all sales</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hasPermission('sales', 'add') && (
            <button onClick={openAddPrevSale} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
              Add Previous Sale
            </button>
          )}
          {hasPermission('sales', 'add') && (
            <button onClick={() => setShowSmartPaste(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
              Smart Paste
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <UiDateRangeBlock
        preset={dateFilter}
        presetOptions={dateFilterOptions}
        onPresetChange={handleDateFilterChange}
        onPrevious={() => shiftSalesDateRange(-1)}
        onNext={() => shiftSalesDateRange(1)}
        periodPicker={viewMode === 'calendar'}
        onPeriodChange={handleSalesCalendarPeriodChange}
        fromDate={startDate}
        onFromDateChange={setStartDate}
        toDate={filterEndCalendarDate}
        onToDateChange={(nextDate) => setEndDate(addCalendarDays(nextDate, -1))}
        fromTime={startTime}
        onFromTimeChange={setStartTime}
        toTime={endTime}
        onToTimeChange={setEndTime}
        showTime
        rangeText={`Selected range: ${formatUiDateTimeRange(startDate, startTime, filterEndCalendarDate, endTime)}`}
        header={(
          <div className="flex flex-wrap items-center gap-2">
            <UiViewToggle
              options={[{ value: 'table', label: 'Table' }, { value: 'calendar', label: 'Calendar' }]}
              value={viewMode}
              onChange={setViewMode}
            />
          </div>
        )}
      />

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <UiSearchField value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sale..." />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none">
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="discounted">Discounted</option>
            <option value="previous">Previous Sales</option>
            <option value="cash">Cash</option>
            <option value="digital">Digital</option>
          </select>
          <select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setCategoryFilter(''); setMenuItemFilter(''); setModifierFilter(''); setModifierOptionFilter(''); }} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none">
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setMenuItemFilter(''); setModifierFilter(''); setModifierOptionFilter(''); }} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none">
            <option value="">All Categories</option>
            {categories.filter(c => !departmentFilter || String(c.department) === departmentFilter).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={menuItemFilter} onChange={(e) => { setMenuItemFilter(e.target.value); setModifierFilter(''); setModifierOptionFilter(''); }} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none">
            <option value="">All Menu Items</option>
            {menuItems.filter(m => !categoryFilter || String(m.category) === categoryFilter).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select value={modifierFilter} onChange={(e) => { setModifierFilter(e.target.value); setModifierOptionFilter(''); }} disabled={!modifierSourceMenuItem || menuItemModifiers.length === 0} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none disabled:bg-gray-100 disabled:text-gray-400">
            <option value="">All Modifiers</option>
            {menuItemModifiers.map(m => <option key={m.id} value={m.id}>{m.display_name || m.name}</option>)}
          </select>
          <select value={modifierOptionFilter} onChange={(e) => setModifierOptionFilter(e.target.value)} disabled={!modifierFilter || modifierOptionOptions.length === 0} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none disabled:bg-gray-100 disabled:text-gray-400">
            <option value="">All Modifier Options</option>
            {modifierOptionOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
          <select value={servingSizeFilter} onChange={(e) => setServingSizeFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm flex-1 sm:flex-none">
            <option value="">All Serving Sizes</option>
            {Array.from(new Set(servingSizeOptions.map(o => o.name).filter(Boolean))).sort().map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>

      {requiresRowData ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
          <span>Loaded {loadedOrderCount.toLocaleString()} of {Math.max(totalOrderCount, loadedOrderCount).toLocaleString()} sale records using paged fetch.</span>
          {ordersTruncated && <span className="text-red-600">Showing the first {SALES_FETCH_LIMIT.toLocaleString()} records for this filter range.</span>}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
          <span>Calendar view is using aggregated totals for faster month and year loading.</span>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <UiSummaryCards>
          <UiSummaryCard label="Total Orders" value={filteredSummary.total_orders} />
          <UiSummaryCard label="Items Sold" value={filteredSummary.total_items_sold} tone="text-blue-600" />
          <UiSummaryCard label="Total Sale" value={formatCurrencyCompact(filteredSummary.total_amount || 0)} tone="text-primary-600" />
          <UiSummaryCard label="Period" lines={[periodParts.from, periodParts.to]} />
        </UiSummaryCards>
      )}

      {viewMode === 'calendar' && renderCalendarView()}

      {/* Collapse/Expand All — only in grouped mode */}
      {viewMode === 'table' && ordersGroupedByDate && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{Math.round(filteredSummary.total_orders)} order{Math.round(filteredSummary.total_orders) !== 1 ? 's' : ''} across {Object.keys(ordersGroupedByDate).length} day{Object.keys(ordersGroupedByDate).length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={expandAll} className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">Expand All</button>
            <button onClick={collapseAll} className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">Collapse All</button>
          </div>
        </div>
      )}

      {searchQuery.trim() && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-800">Search Results</h3>
            <span className="text-xs text-gray-400">{salesSearchSummaryRows.length} item{salesSearchSummaryRows.length !== 1 ? 's' : ''}</span>
          </div>
          {salesSearchSummaryRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <SortableHeader label="Item Name" col="itemName" sortKey={srSortKey} sortDir={srSortDir} onSort={srHandleSort} />
                    <SortableHeader label="Modifier / Options Found" col="modifiersText" sortKey={srSortKey} sortDir={srSortDir} onSort={srHandleSort} />
                    <SortableHeader label="Count" col="count" sortKey={srSortKey} sortDir={srSortDir} onSort={srHandleSort} right />
                    <SortableHeader label="Total" col="total" sortKey={srSortKey} sortDir={srSortDir} onSort={srHandleSort} right />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedSalesSearchSummaryRows.map(row => (
                    <tr key={row.itemName}>
                      <td className="px-4 py-3 font-medium text-gray-800">{row.itemName}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {row.modifierLabels.length ? row.modifierLabels.join(', ') : 'No modifier option'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.count.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary-600">{formatCurrencyDetailed(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">No matching sale items found</div>
          )}
        </div>
      )}

      {/* Orders Table - Desktop */}
      {viewMode === 'table' && <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader label="Sale #" col="order_number" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Date/Time" col="created_at" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Items" col="item_count" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Total" col="total_amount" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Payment" col="payment_method" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Status" col="status" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <SortableHeader label="Cashier" col="cashier_name" sortKey={oSortKey} sortDir={oSortDir} onSort={oHandleSort} />
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {ordersGroupedByDate ? (
              Object.entries(ordersGroupedByDate).map(([date, dateOrders]) => {
                const isCollapsed = collapsedDates.has(date);
                const holiday = getHolidayForDate(date);
                const groupTotal = dateOrders.reduce((s, o) => s + getSignedOrderAmount(o), 0);
                const completedOrderCount = dateOrders.reduce((s, o) => s + getOrderCountFactor(o), 0);
                return (
                  <React.Fragment key={date}>
                    <tr className={`${holiday ? 'bg-amber-50 hover:bg-amber-100' : 'bg-gray-100 hover:bg-gray-200'} cursor-pointer select-none`} onClick={() => toggleDate(date)}>
                      <td colSpan={8} className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <ChevronDown size={15} className={`text-gray-500 transition-transform flex-shrink-0 ${isCollapsed ? '-rotate-90' : ''}`} />
                          <span className="font-semibold text-gray-800 text-sm">{fmtDateGroup(date)}</span>
                          {holiday && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">{holiday.name}</span>}
                          <span className="text-xs text-gray-400">{completedOrderCount} sale{completedOrderCount !== 1 ? 's' : ''}</span>
                          <span className="ml-auto font-bold text-primary-600 text-sm">{formatCurrencyCompact(groupTotal)}</span>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && dateOrders.map(order => (
                      <tr key={order.id} className={`hover:bg-gray-50 ${isPreviousSale(order) ? 'bg-purple-50' : parseFloat(order.discount_amount || 0) > 0 ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3"><div className="font-medium text-gray-800 truncate max-w-xs">{order.items?.map(i => i.menu_item_name).join(', ').substring(0, 50)}{order.items?.map(i => i.menu_item_name).join(', ').length > 50 ? '...' : ''}</div><div className={`text-xs ${isPreviousSale(order) ? 'text-purple-600 font-medium' : parseFloat(order.discount_amount || 0) > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>#{order.order_number}{isPreviousSale(order) ? ' (Previous)' : ''}</div></td>
                        <td className="px-4 py-3 text-sm text-gray-600"><div className="text-xs text-gray-400">{formatUiTime(order.created_at)}</div></td>
                        <td className="px-4 py-3 text-sm">{getSignedOrderItemCount(order)} items</td>
                        <td className={`px-4 py-3 font-medium ${getOrderSalesFactor(order) < 0 ? 'text-red-600' : 'text-primary-600'}`}>{formatCurrencyDetailed(getSignedOrderAmount(order))}</td>
                        <td className="px-4 py-3 text-sm capitalize">{order.payment_method?.replace('_', ' ')}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>{order.status}</span></td>
                        <td className="px-4 py-3 text-sm">{order.cashier_name}</td>
                        <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                          <button onClick={() => viewOrder(order)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="View"><Eye size={16} /></button>
                          {order.status === 'pending' && <button onClick={() => openCompleteModal(order)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Complete Sale"><Play size={16} /></button>}
                          {order.status !== 'cancelled' && hasPermission('sales', 'edit') && <button onClick={() => openEditModal(order)} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Edit Sale"><Edit2 size={16} /></button>}
                          {order.status === 'completed' && hasPermission('sales', 'edit') && <button onClick={() => cancelOrder(order)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Cancel"><XCircle size={16} /></button>}
                          {hasPermission('sales', 'delete') && <button onClick={() => deleteSale(order)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete (erase record)"><Trash2 size={16} /></button>}
                        </div></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              filteredOrders.map((order) => (
                <tr key={order.id} className={`hover:bg-gray-50 ${isPreviousSale(order) ? 'bg-purple-50' : parseFloat(order.discount_amount || 0) > 0 ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 truncate max-w-xs">
                      {order.items?.map(item => item.menu_item_name).join(', ').substring(0, 50)}{order.items?.map(item => item.menu_item_name).join(', ').length > 50 ? '...' : ''}
                    </div>
                    <div className={`text-xs ${isPreviousSale(order) ? 'text-purple-600 font-medium' : parseFloat(order.discount_amount || 0) > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>#{order.order_number}{isPreviousSale(order) ? ' (Previous)' : ''}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>{formatUiDate(order.created_at)}</div>
                    <div className="text-xs text-gray-400">{formatUiTime(order.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{getSignedOrderItemCount(order)} items</td>
                  <td className={`px-4 py-3 font-medium ${getOrderSalesFactor(order) < 0 ? 'text-red-600' : 'text-primary-600'}`}>{formatCurrencyDetailed(getSignedOrderAmount(order))}</td>
                  <td className="px-4 py-3 text-sm capitalize">{order.payment_method?.replace('_', ' ')}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>{order.status}</span></td>
                  <td className="px-4 py-3 text-sm">{order.cashier_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => viewOrder(order)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="View"><Eye size={16} /></button>
                      {order.status === 'pending' && <button onClick={() => openCompleteModal(order)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Complete Sale"><Play size={16} /></button>}
                      {order.status !== 'cancelled' && hasPermission('sales', 'edit') && <button onClick={() => openEditModal(order)} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Edit Sale"><Edit2 size={16} /></button>}
                      {order.status === 'completed' && hasPermission('sales', 'edit') && <button onClick={() => cancelOrder(order)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Cancel"><XCircle size={16} /></button>}
                      {hasPermission('sales', 'delete') && <button onClick={() => deleteSale(order)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete (erase record)"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500">No sales found</div>
        )}
      </div>}

      {/* Orders Cards - Mobile */}
      {viewMode === 'table' && <div className="md:hidden space-y-3">
        {ordersGroupedByDate ? (
          Object.entries(ordersGroupedByDate).map(([date, dateOrders]) => {
            const isCollapsed = collapsedDates.has(date);
            const holiday = getHolidayForDate(date);
            const groupTotal = dateOrders.reduce((s, o) => s + getSignedOrderAmount(o), 0);
            const completedOrderCount = dateOrders.reduce((s, o) => s + getOrderCountFactor(o), 0);
            return (
              <div key={date}>
                <button onClick={() => toggleDate(date)} className={`w-full flex items-center justify-between p-3 rounded-xl border mb-1 text-left ${holiday ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                  <div className="flex items-center gap-2">
                    <ChevronDown size={14} className={`text-gray-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    <span className="font-semibold text-gray-800 text-sm">{fmtDateGroup(date)}</span>
                    {holiday && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">{holiday.name}</span>}
                    <span className="text-xs text-gray-400">{completedOrderCount} sales</span>
                  </div>
                  <span className="font-bold text-primary-600 text-sm">{formatCurrencyCompact(groupTotal)}</span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-2 pl-2">
                    {dateOrders.map(order => (
                      <div key={order.id} className={`bg-white rounded-xl shadow-sm p-4 border ${isPreviousSale(order) ? 'border-purple-300 bg-purple-50' : parseFloat(order.discount_amount || 0) > 0 ? 'border-red-300 bg-red-50' : ''}`} onClick={() => viewOrder(order)}>
            <div className="flex items-center justify-between mb-2">
              <div className="min-w-0 flex-1 mr-2">
                <div className="font-semibold text-gray-800 truncate text-sm">
                  {order.items?.map(item => item.menu_item_name).join(', ').substring(0, 28)}{order.items?.map(item => item.menu_item_name).join(', ').length > 28 ? '...' : ''}
                </div>
                <div className={`text-xs ${isPreviousSale(order) ? 'text-purple-600 font-medium' : parseFloat(order.discount_amount || 0) > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>#{order.order_number}{isPreviousSale(order) ? ' (Previous)' : ''}</div>
              </div>
              <span className={`flex-shrink-0 px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>
                {order.status}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xl font-bold ${getOrderSalesFactor(order) < 0 ? 'text-red-600' : 'text-primary-600'}`}>{formatCurrencyDetailed(getSignedOrderAmount(order))}</span>
              <span className="text-sm text-gray-500 capitalize">{order.payment_method?.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{formatUiTime(order.created_at)}</span>
              <span>{getSignedOrderItemCount(order)} items</span>
              <span>{order.cashier_name}</span>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t flex-wrap">
              <button onClick={(e) => { e.stopPropagation(); viewOrder(order); }} className="flex-1 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg font-medium flex items-center justify-center gap-1">
                View
              </button>
              {order.status === 'pending' && (
                <button onClick={(e) => { e.stopPropagation(); openCompleteModal(order); }} className="flex-1 py-2 text-sm text-green-600 bg-green-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Complete
                </button>
              )}
              {order.status !== 'cancelled' && hasPermission('sales', 'edit') && (
                <button onClick={(e) => { e.stopPropagation(); openEditModal(order); }} className="flex-1 py-2 text-sm text-orange-600 bg-orange-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Edit
                </button>
              )}
              {order.status === 'completed' && hasPermission('sales', 'edit') && (
                <button onClick={(e) => { e.stopPropagation(); cancelOrder(order); }} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Cancel
                </button>
              )}
              {hasPermission('sales', 'delete') && (
                <button onClick={(e) => { e.stopPropagation(); deleteSale(order); }} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Delete
                </button>
              )}
            </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className={`bg-white rounded-xl shadow-sm p-4 border ${isPreviousSale(order) ? 'border-purple-300 bg-purple-50' : parseFloat(order.discount_amount || 0) > 0 ? 'border-red-300 bg-red-50' : ''}`} onClick={() => viewOrder(order)}>
              <div className="flex items-center justify-between mb-2">
                <div className="min-w-0 flex-1 mr-2">
                  <div className="font-semibold text-gray-800 truncate text-sm">{order.items?.map(item => item.menu_item_name).join(', ').substring(0, 28)}{order.items?.map(item => item.menu_item_name).join(', ').length > 28 ? '...' : ''}</div>
                  <div className={`text-xs ${isPreviousSale(order) ? 'text-purple-600 font-medium' : parseFloat(order.discount_amount || 0) > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>#{order.order_number}{isPreviousSale(order) ? ' (Previous)' : ''}</div>
                </div>
                <span className={`flex-shrink-0 px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>{order.status}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xl font-bold text-primary-600">{formatCurrencyDetailed(order.total_amount)}</span>
                <span className="text-sm text-gray-500 capitalize">{order.payment_method?.replace('_', ' ')}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{formatUiTime(order.created_at)}</span>
                <span>{order.items?.reduce((sum, i) => sum + i.quantity, 0) || 0} items</span>
                <span>{order.cashier_name}</span>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t flex-wrap">
                <button onClick={(e) => { e.stopPropagation(); viewOrder(order); }} className="flex-1 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg font-medium flex items-center justify-center gap-1">View</button>
                {order.status === 'pending' && <button onClick={(e) => { e.stopPropagation(); openCompleteModal(order); }} className="flex-1 py-2 text-sm text-green-600 bg-green-50 rounded-lg font-medium flex items-center justify-center gap-1">Complete</button>}
                {order.status !== 'cancelled' && hasPermission('sales', 'edit') && <button onClick={(e) => { e.stopPropagation(); openEditModal(order); }} className="flex-1 py-2 text-sm text-orange-600 bg-orange-50 rounded-lg font-medium flex items-center justify-center gap-1">Edit</button>}
                {order.status === 'completed' && hasPermission('sales', 'edit') && <button onClick={(e) => { e.stopPropagation(); cancelOrder(order); }} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">Cancel</button>}
                {hasPermission('sales', 'delete') && <button onClick={(e) => { e.stopPropagation(); deleteSale(order); }} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">Delete</button>}
              </div>
            </div>
          ))
        )}
        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl">No sales found</div>
        )}
      </div>}

      {/* Order Detail Modal */}
      {showModal && selectedOrder && (
        <OrderReceiptModal
          order={selectedOrder}
          settings={settings}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Edit Sale Modal */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Amend Sale</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Sale #: {selectedOrder.order_number}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={editData.payment_method}
                  onChange={(e) => setEditData(p => ({ ...p, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="digital_wallet">Digital Wallet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
                <input
                  type="number"
                  value={editData.discount_percent}
                  onChange={(e) => setEditData(p => ({ ...p, discount_percent: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  min="0" max="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData(p => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  rows="2"
                  placeholder="Add notes..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">
                  Cancel
                </button>
                <button onClick={handleEditSale} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Held Sale Modal */}
      {showCompleteModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Complete Sale</h3>
              <button onClick={() => setShowCompleteModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">Sale #: {selectedOrder.order_number}</p>
            <div className="bg-primary-50 rounded-xl p-3 text-center mb-4">
              <p className="text-sm text-primary-600">Total Amount</p>
              <p className="text-2xl font-bold text-primary-700">{formatCurrencyDetailed(selectedOrder.total_amount)}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {['cash', 'card', 'digital_wallet'].map((m) => (
                    <button
                      key={m}
                      onClick={() => setCompleteData(p => ({ ...p, payment_method: m }))}
                      className={`py-2 px-3 rounded-lg border-2 text-sm font-medium capitalize transition ${
                        completeData.payment_method === m ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200'
                      }`}
                    >
                      {m === 'digital_wallet' ? 'Digital' : m}
                    </button>
                  ))}
                </div>
              </div>

              {completeData.payment_method === 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received</label>
                  <input
                    type="number"
                    value={completeData.amount_received}
                    onChange={(e) => setCompleteData(p => ({ ...p, amount_received: e.target.value }))}
                    placeholder={parseFloat(selectedOrder.total_amount).toFixed(2)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                  {completeData.amount_received && parseFloat(completeData.amount_received) >= parseFloat(selectedOrder.total_amount) && (
                    <p className="mt-1 text-green-600 text-sm font-medium">
                      Change: {formatCurrencyDetailed(parseFloat(completeData.amount_received) - parseFloat(selectedOrder.total_amount))}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowCompleteModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">
                  Cancel
                </button>
                <button onClick={completeHeldSale} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                  Complete Sale
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Smart Paste Modal */}
      {showSmartPaste && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowSmartPaste(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Smart Paste Sales</h3>
              <button onClick={() => setShowSmartPaste(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Sale Date</label>
                <UiDateField value={smartPasteDate} onChange={setSmartPasteDate} />
              </div>

              {smartParsedRows.length === 0 ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Paste sales text</label>
                    <textarea
                      value={smartPasteText}
                      onChange={e => setSmartPasteText(e.target.value)}
                      rows={8}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder={"Mango Shake  2  350  14:30\nStrawberry Juice  250  14:30\n550  15:00"}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Format: menu item, count (optional), total price, time (optional). One entry per line.</p>
                  </div>
                  <button
                    onClick={parseSmartPaste}
                    disabled={smartParsing}
                    className="w-full py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                  >{smartParsing ? 'Parsing...' : 'Parse & Review'}</button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">{smartParsedRows.length} row(s) parsed. Review and correct below:</p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-600">Menu Item</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-16">Qty</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-24">Price</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 w-24">Time</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {smartParsedRows.map((row, idx) => (
                          <tr key={idx} className={row.matched_id ? '' : 'bg-red-50'}>
                            <td className="px-2 py-2">
                              <select
                                value={row.matched_id || ''}
                                onChange={e => updateSmartRow(idx, 'matched_id', e.target.value)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm"
                              >
                                <option value="">{row.item_name || 'Select item...'}</option>
                                {(smartPasteMenuItems || []).map(mi => (
                                  <option key={mi.id} value={mi.id}>{mi.name} ({formatCurrencyDetailed(mi.selling_price)})</option>
                                ))}
                              </select>
                              {row.modifier_text && <p className="text-[11px] text-purple-600 mt-0.5">Modifier: {row.modifier_text}</p>}
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number" min="1" value={row.quantity}
                                onChange={e => updateSmartRow(idx, 'quantity', parseInt(e.target.value) || 1)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-center"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number" step="0.01" value={row.total_price || ''}
                                onChange={e => updateSmartRow(idx, 'total_price', parseFloat(e.target.value) || null)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-center"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <UiTimeField
                                value={row.time || ''}
                                onChange={v => updateSmartRow(idx, 'time', v)}
                                inputClassName="!py-1"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <button onClick={() => removeSmartRow(idx)} className="p-1 text-red-400 hover:text-red-600"><X size={14} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setSmartParsedRows([]); }}
                      className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition"
                    >Back to Paste</button>
                    <button
                      onClick={submitSmartPaste}
                      disabled={smartSubmitting || !smartParsedRows.some(r => r.matched_id)}
                      className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                    >{smartSubmitting ? 'Submitting...' : `Submit ${smartParsedRows.filter(r => r.matched_id).length} Sale(s)`}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
